/**
 * PR-F — Dimension entity → DimensionPrimitive[] adapter.
 *
 * LEGACY `EntityPreview.properties` string-bag adapter. Reads the DIMVAR /
 * geometry fields back out of the bag and delegates the actual primitive
 * math to the SHARED builder (`../adapters/geom/dimensionGeometry.ts`) — the
 * TYPED sibling (`../adapters/typed/dimensionFromEntity.ts`, DEFUSE Lane H /
 * S8) extracts the SAME raw fields directly off the canonical
 * `DimensionEntity` and calls the identical shared builder, so the two
 * adapters cannot diverge on the geometry math itself, only on where the
 * raw numbers come from.
 *
 * Still reachable for: the `BAG_FALLBACK_RENDER_KINDS`-era draft-preview
 * channel (`draft/draftPreviewLayer.tsx`, an in-flight DIMENSION draft has
 * no typed document entity yet) and the PDF export converter
 * (`print/pdf/converters/dimensionConverter.ts`), which both still feed a
 * bag `EntityPreview`.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * Subtype semantics, DIMASZ / DIMTXT / DIMGAP / DIMEXO / DIMEXE / DIMSE1/2 /
 * DIMTIH / DIMBLK gating match the SVG renderer 1:1 (unchanged by the S8
 * extraction — see `../adapters/geom/dimensionGeometry.ts` for the math).
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber } from "../../scene/renderHelpers";
import {
  readDimStyle,
  parseDimensionSubtype,
} from "../../scene/dimensionHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import {
  buildRenderableShell,
  resolveShellColorHex,
  type AdapterContext,
} from "../adapters/adapterContext";
import {
  applyDimensionAnnotativeOverride,
  buildDimensionGeometry,
  finalizeDimensionBbox,
  toDimensionSceneGeometry,
  type DimensionRawFields,
} from "../adapters/geom/dimensionGeometry";
import { applyOffset } from "../coordinateOrigin";

function vec3(x: number, y: number, z: number = 0): Vec3 {
  return { x, y, z };
}

/** Extract the shared raw-field shape from the legacy `properties` bag. */
function extractRawFields(props: Record<string, string>): DimensionRawFields {
  return {
    ext1: {
      x: readNumber(props, "Extension 1 X"),
      y: readNumber(props, "Extension 1 Y"),
    },
    ext2: {
      x: readNumber(props, "Extension 2 X"),
      y: readNumber(props, "Extension 2 Y"),
    },
    dimLine: {
      x: readNumber(props, "Dimension line X"),
      y: readNumber(props, "Dimension line Y"),
    },
    thirdPoint: {
      x: readNumber(props, "Third point X"),
      y: readNumber(props, "Third point Y"),
    },
    measurement: readNumber(props, "Measurement"),
    textOverride: props["Text override"] ?? "",
    textHeightRaw: readNumber(props, "Text height"),
    // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the real
    // DXF group-53 text_rotation override, now bag-carried
    // (`state/dxfGeometry.ts`'s `buildDimensionPreview`) so this LEGACY
    // path (draft-preview channel + PDF export converter) observes a
    // MIRRTEXT-mirrored (or directly authored) dimension's text angle,
    // matching the typed adapter.
    textRotationOverride: readNumber(props, "Text angle relative to dim line"),
    textMirrored: props["Text mirrored"] === "1",
  };
}

/* ── Public entry point ──────────────────────────────────────────── */

/**
 * Dimension entity → `RenderableEntity` (with `kind: "dimension"`
 * geometry). The renderer reads `geometry.primitives` and feeds each
 * to the corresponding line / arrow / text pipeline.
 *
 * Annotative scale resolution mirrors the SVG renderer: when the
 * entity carries `__dimension_annotative_contexts` and an active
 * scale is in effect, the resolved style overrides DIMTXT / DIMASZ.
 *
 * `annotationScale` is read from the property bag (PR-I will move
 * this onto the AdapterContext).
 */
export function dimensionToPrimitives(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const style = readDimStyle(entity.properties);

  // Annotative override (mirror renderDimension.ts:704+).
  const dimAnnoRaw = entity.properties["DIMANNO"];
  const annotative =
    dimAnnoRaw !== undefined && dimAnnoRaw !== "0" && dimAnnoRaw !== "";
  let contexts: Array<{
    scale: number;
    textHeight?: number;
    arrowheadSize?: number;
  }> = [];
  try {
    contexts = JSON.parse(
      entity.properties["__dimension_annotative_contexts"] ?? "[]",
    );
  } catch {
    contexts = [];
  }
  const annoScale = Number(entity.properties["__active_annotation_scale"]);
  const resolved = applyDimensionAnnotativeOverride(
    style,
    annotative,
    contexts,
    Number.isFinite(annoScale) ? annoScale : null,
  );

  const colorHex = resolveShellColorHex(entity, ctx);

  const subtype = parseDimensionSubtype(entity.properties.Subtype ?? "Linear");
  const raw = extractRawFields(entity.properties);
  const result = buildDimensionGeometry(raw, subtype, resolved, colorHex);

  const fallback = applyOffset(vec3(raw.dimLine.x, raw.dimLine.y));
  const bbox = finalizeDimensionBbox(result.bbox, fallback);

  const geometry = toDimensionSceneGeometry(result.primitives);
  return buildRenderableShell(entity, ctx, geometry, bbox);
}
